import { useState } from "react";
import { SeoContent } from "../components/SeoContent";

type Mark = -1 | 0 | 1;
type TutorialTool = "fill" | "cross";

const COLUMN_CLUES = ["2", "4", "4", "4", "2"];
const ROW_CLUES = ["1 1", "5", "5", "3", "1"];
const INITIAL_MARKS: Mark[] = [
  0, 1, 0, 1, 0,
  1, 1, 0, 1, 1,
  1, 0, 0, 0, 0,
  0, 0, 0, 0, 0,
  0, 0, 0, 0, 0,
];

const STEPS: Array<{
  title: string;
  instruction: string;
  targets: number[];
  expected: Exclude<Mark, 0>;
  activeClue: { axis: "column" | "row"; index: number };
  success: string;
}> = [
  {
    title: "Read a row clue",
    instruction: "The 5 means every square in this row is filled. Fill the outlined gap.",
    targets: [7],
    expected: 1,
    activeClue: { axis: "row", index: 1 },
    success: "Good. A clue of 5 fills the whole row.",
  },
  {
    title: "Close a completed group",
    instruction: "The first column’s 2 is complete. Choose Cross, then mark the three squares outside that group.",
    targets: [0, 15, 20],
    expected: -1,
    activeClue: { axis: "column", index: 0 },
    success: "Exactly. Crosses show where filled groups cannot continue.",
  },
  {
    title: "Combine what you know",
    instruction: "Row 1 needs two separate groups of 1. Both filled squares are known, so cross its two remaining open squares.",
    targets: [2, 4],
    expected: -1,
    activeClue: { axis: "row", index: 0 },
    success: "Lesson complete. You used row and column clues together.",
  },
];

function cellLabel(index: number, mark: Mark, isTarget: boolean): string {
  const row = Math.floor(index / 5) + 1;
  const column = (index % 5) + 1;
  const state = mark === 1 ? "filled" : mark === -1 ? "crossed" : "empty";
  return `Row ${row}, column ${column}, ${state}${isTarget ? ", current lesson target" : ""}`;
}

export function HowToPlay() {
  const [marks, setMarks] = useState<Mark[]>(INITIAL_MARKS);
  const [tool, setTool] = useState<TutorialTool>("fill");
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [status, setStatus] = useState("Start with the outlined square.");
  const lesson = STEPS[step]!;

  function makeMove(index: number) {
    if (completed) return;
    if (!lesson.targets.includes(index)) {
      setStatus("Work on the outlined squares for this step.");
      return;
    }

    const expectedTool: TutorialTool = lesson.expected === 1 ? "fill" : "cross";
    if (tool !== expectedTool) {
      setStatus(`Choose ${expectedTool === "fill" ? "Fill" : "Cross"} first.`);
      return;
    }

    const nextMarks = [...marks];
    nextMarks[index] = lesson.expected;
    setMarks(nextMarks);

    if (!lesson.targets.every((target) => nextMarks[target] === lesson.expected)) {
      const remaining = lesson.targets.filter((target) => nextMarks[target] !== lesson.expected).length;
      setStatus(`${remaining} outlined ${remaining === 1 ? "square" : "squares"} left.`);
      return;
    }

    setStatus(lesson.success);
    if (step === STEPS.length - 1) setCompleted(true);
    else setStep((current) => current + 1);
  }

  function restart() {
    setMarks([...INITIAL_MARKS]);
    setTool("fill");
    setStep(0);
    setCompleted(false);
    setStatus("Start with the outlined square.");
  }

  return (
    <main className="prose-page tutorial-page" id="main-content">
      <p className="eyebrow">Learn by doing</p>
      <h1>How to play Nonogram</h1>
      <p className="lede">Use number clues to find groups of filled squares. Try the three short moves below, then take the same logic into a full puzzle.</p>

      <div className="tutorial-stage" role="region" aria-labelledby="tutorial-title">
        <div className="tutorial-board-wrap">
          <div className="tutorial-board" aria-label="Interactive 5 by 5 Nonogram lesson">
            <span className="tutorial-corner">5×5</span>
            <div className="tutorial-column-clues" aria-label="Column clues">
              {COLUMN_CLUES.map((clue, index) => (
                <span className={lesson.activeClue.axis === "column" && lesson.activeClue.index === index ? "is-active" : ""} key={index}>{clue}</span>
              ))}
            </div>
            <div className="tutorial-row-clues" aria-label="Row clues">
              {ROW_CLUES.map((clue, index) => (
                <span className={lesson.activeClue.axis === "row" && lesson.activeClue.index === index ? "is-active" : ""} key={index}>{clue}</span>
              ))}
            </div>
            <div className="tutorial-grid">
              {marks.map((mark, index) => {
                const isTarget = !completed && lesson.targets.includes(index) && mark !== lesson.expected;
                return (
                  <button
                    className={`tutorial-cell${mark === 1 ? " is-fill" : mark === -1 ? " is-cross" : ""}${isTarget ? " is-target" : ""}`}
                    type="button"
                    aria-label={cellLabel(index, mark, isTarget)}
                    aria-describedby="tutorial-instruction tutorial-status"
                    key={index}
                    onClick={() => makeMove(index)}
                  >
                    {mark === -1 ? <span aria-hidden="true">×</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="tutorial-coach">
          {completed ? (
            <>
              <p className="eyebrow">Picture logic unlocked</p>
              <h2 id="tutorial-title">You’re ready to solve</h2>
              <p id="tutorial-instruction">Numbers describe filled groups in order. Crosses protect the spaces you know must stay empty.</p>
              <div className="tutorial-actions">
                <a className="primary-button" href="/nonogram/easy">Play an easy 5×5</a>
                <button className="text-button" type="button" onClick={restart}>Restart lesson</button>
              </div>
            </>
          ) : (
            <>
              <p className="eyebrow">Step {step + 1} of {STEPS.length}</p>
              <h2 id="tutorial-title">{lesson.title}</h2>
              <p id="tutorial-instruction">{lesson.instruction}</p>
              <div className="tutorial-tools" role="group" aria-label="Choose a marking tool">
                <button className={tool === "fill" ? "tool-button is-active" : "tool-button"} type="button" aria-pressed={tool === "fill"} onClick={() => setTool("fill")}>
                  <span className="tool-symbol" aria-hidden="true">■</span> Fill
                </button>
                <button className={tool === "cross" ? "tool-button is-active" : "tool-button"} type="button" aria-pressed={tool === "cross"} onClick={() => setTool("cross")}>
                  <span className="tool-symbol" aria-hidden="true">×</span> Cross
                </button>
              </div>
              <div className="tutorial-progress" aria-hidden="true">
                {STEPS.map((_, index) => <i className={index <= step ? "is-reached" : ""} key={index} />)}
              </div>
            </>
          )}
          <p className="tutorial-status" id="tutorial-status" role="status" aria-live="polite">{status}</p>
        </div>
      </div>

      <div className="tutorial-note">
        <p><strong>Remember:</strong> clues stay in order, separate groups need at least one empty square, and every confident mark helps the crossing clues.</p>
      </div>

      {/* The lesson answers "how do I play this one"; the questions below answer the
          ones people arrive with, and they are the same set the page publishes as
          FAQ structured data. */}
      <SeoContent path="/how-to-play-nonogram" />
    </main>
  );
}
